// src/pages/AccountPage.js
import React, { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getMe } from "../services/users.api";
import { getMyOrders } from "../services/orders.api";

function AccountPage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const data = await getMe();

        const rawPhone = data.phone || "";
        const rawEmail = data.email || "";
        const phoneLooksLikeEmail = rawPhone.includes("@");

        setUser({
          ...data,
          phone: phoneLooksLikeEmail ? "" : rawPhone,
          email: rawEmail || (phoneLooksLikeEmail ? rawPhone : ""),
        });

        localStorage.setItem("esill-user", JSON.stringify(data));

        const orders = await getMyOrders();
        if (orders.length) setLastOrder(orders[0]);
      } catch {
        setUser(null);
      }
    }
    load();
  }, []);

  const TEXT = {
    EN: {
      title: "MY ACCOUNT",
      noUser: "No account found. Create your profile to continue.",
      create: "Create Account",
      details: "Account Details",
      edit: "Edit Profile",
      name: "Full Name",
      phone: "Phone Number",
      email: "Email",
      city: "City",
      address: "Address",
      noEmail: "No email saved",
      lastOrder: "Last Order",
      noOrder: "You have no recent orders.",
      status: "Order Status",
      myOrders: "My Orders",
    },
    AR: {
      title: "حسابي",
      noUser: "لا يوجد حساب بعد. قومي بإنشاء حساب للمتابعة.",
      create: "إنشاء حساب",
      details: "بيانات الحساب",
      edit: "تعديل الملف",
      name: "الاسم الكامل",
      phone: "رقم الهاتف",
      email: "البريد الإلكتروني",
      city: "المدينة",
      address: "العنوان",
      noEmail: "لا يوجد بريد إلكتروني محفوظ",
      lastOrder: "آخر طلب",
      noOrder: "لا يوجد طلبات حديثة.",
      status: "حالة الطلب",
      myOrders: "طلباتي",
    },
  };

  const t = TEXT[lang];

  return (
    <div
      className="w-full min-h-screen bg-[#0F1A12] pt-[95px] px-5 pb-24 text-white"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      {/* BACK */}
      <button
        onClick={() => navigate("/")}
        className="absolute top-[80px] left-4 text-3xl opacity-70 hover:opacity-100"
      >
        ×
      </button>

      {/* TITLE */}
      <h1 className="text-xl font-bold text-center tracking-wide">
        {t.title}
      </h1>
      <div className="h-[2px] w-20 bg-[#2E5A2A] mx-auto mt-3 mb-8"></div>

      {/* NO ACCOUNT */}
      {!user && (
        <div className="text-center mt-20">
          <p className="text-[#9FB39C] mb-6">{t.noUser}</p>
          <button
            onClick={() => navigate("/create-account")}
            className="px-8 py-3 bg-[#2E5A2A] text-white rounded-md font-semibold hover:brightness-110 transition"
          >
            {t.create}
          </button>
        </div>
      )}

      {/* ACCOUNT */}
      {user && (
        <div className="w-full max-w-xl mx-auto space-y-8">

          {/* ACCOUNT DETAILS */}
          <div className="border border-[#2E5A2A] rounded-lg p-5 bg-black/70">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-lg">{t.details}</h2>
              <button
                onClick={() => navigate("/edit-profile")}
                className="
                  px-4 py-1 text-xs font-semibold
                  border border-[#2E5A2A]
                  rounded-full
                  hover:bg-[#2E5A2A]
                  transition
                "
              >
                {t.edit}
              </button>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-[#1F3324] pb-1"><span>{t.name}</span><span>{user.full_name}</span></div>
              <div className="flex justify-between border-b border-[#1F3324] pb-1"><span>{t.phone}</span><span>{user.phone}</span></div>
              <div className="flex justify-between border-b border-[#1F3324] pb-1"><span>{t.email}</span><span>{user.email || t.noEmail}</span></div>
              <div className="flex justify-between border-b border-[#1F3324] pb-1"><span>{t.city}</span><span>{user.city}</span></div>
              <div className="flex justify-between"><span>{t.address}</span><span>{user.address}</span></div>
            </div>
          </div>

          {/* LAST ORDER */}
          <div className="border border-[#2E5A2A] rounded-lg p-5 bg-black/70">
            <h2 className="font-semibold text-lg mb-4">{t.lastOrder}</h2>

            {!lastOrder && (
              <p className="text-sm text-[#9FB39C]">{t.noOrder}</p>
            )}

            {lastOrder && (
              <div
                onClick={() => navigate("/orders")}
                className="space-y-2 text-sm cursor-pointer hover:opacity-90"
              >
                <div className="flex justify-between"><span>Order #</span><span>{lastOrder.order_number}</span></div>
                <div className="flex justify-between">
                  <span>{t.status}</span>
                  <span className="text-[#2E5A2A] font-semibold">
                    {lastOrder.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Date</span>
                  <span>{new Date(lastOrder.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            )}
          </div>

          {/* ACTIONS */}
          <button
            onClick={() => navigate("/orders")}
            className="w-full py-3 bg-[#2E5A2A] rounded-md font-semibold hover:brightness-110 transition"
          >
            {t.myOrders}
          </button>

          <button
            onClick={() => navigate("/contact")}
            className="w-full py-3 border border-[#2E5A2A] rounded-md font-semibold hover:bg-[#2E5A2A] transition"
          >
            {lang === "EN" ? "Support / Messages" : "الدعم والرسائل"}
          </button>
        </div>
      )}
    </div>
  );
}

export default AccountPage;
